import { useEffect } from 'react';
import { useSEO } from '../../../utils/seo';
import Navbar from '../../home/components/Navbar';
import Footer from '../../home/components/Footer';
import WhatsAppButton from '../../home/components/WhatsAppButton';

export default function HasarDanismanligiPage() {
  useSEO({
    title: 'Hasar Danışmanlığı ve Kaza Onarımı | Denizli Araç Servisi',
    description: 'Denizli\'de profesyonel hasar danışmanlığı ve kaza onarımı hizmetleri. Sigorta süreçlerinden teknik onarıma kadar her aşamada yanınızdayız. 15 yıllık deneyim ile güvenilir hizmet.',
    keywords: 'denizli hasar danışmanlığı, denizli kaza onarımı, denizli araç hasarı, denizli sigorta onarımı, denizli kaporta boyası, araç kaza tamiri denizli',
    canonical: '/blog/hasar-danismanligi-kaza-onarimi',
    schema: {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: 'Hasar Danışmanlığı & Kaza Onarımı',
      description: 'Profesyonel hasar tespiti ve onarım süreçlerinde yanınızdayız. Sigorta süreçlerinden teknik onarıma kadar her aşamada destek.',
      author: {
        '@type': 'Organization',
        name: 'D-Atolye Premium Otomotiv Servisi',
      },
      publisher: {
        '@type': 'Organization',
        name: 'D-Atolye Premium Otomotiv Servisi',
        logo: {
          '@type': 'ImageObject',
          url: 'https://static.readdy.ai/image/5946f8dc6d1689a139aced3671fb829b/5627d08f05dce4eda6468c71266b611d.png',
        },
      },
      datePublished: '2024-01-15',
      dateModified: new Date().toISOString().split('T')[0],
      mainEntityOfPage: {
        '@type': 'WebPage',
        '@id': `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/blog/hasar-danismanligi-kaza-onarimi`,
      },
    },
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative h-[500px] flex items-center justify-center overflow-hidden">
        <img
          src="https://public.readdy.ai/ai/img_res/b4c679a25b31d8d3c0f4b0818f40c68a.jpg"
          alt="Hasar Danışmanlığı"
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/60"></div>
        <div className="relative z-10 text-center text-white px-6 max-w-4xl mx-auto">
          <h1 className="font-serif text-5xl lg:text-6xl font-bold mb-6">Hasar Danışmanlığı &amp; Kaza Onarımı</h1>
          <p className="font-sans text-xl text-gray-200">Profesyonel hasar tespiti ve onarım süreçlerinde yanınızdayız</p>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-12">
          
          {/* Introduction */}
          <div className="mb-16">
            <p className="font-sans text-lg text-gray-700 leading-relaxed mb-6">
              Araç kazaları ve hasarlar, her sürücünün karşılaşabileceği stresli durumlardan biridir. Servisimiz, 15 yıllık deneyimi ve uzman kadrosuyla, kaza sonrası tüm süreçlerde yanınızda olarak aracınızın eski haline dönmesini sağlar. Hasar danışmanlığı ve kaza onarımı hizmetlerimiz, sigorta süreçlerinden teknik onarıma kadar her aşamayı kapsar.
            </p>
          </div>

          {/* Hasar Danışmanlığı Nedir? */}
          <div className="mb-16">
            <h2 className="font-serif text-3xl font-bold text-gray-900 mb-6">Hasar Danışmanlığı Nedir?</h2>
            <p className="font-sans text-base text-gray-700 leading-relaxed mb-4">
              Hasar danışmanlığı, kaza sonrası aracınızın hasarının profesyonel olarak tespit edilmesi, değerlendirilmesi ve sigorta şirketleriyle yapılacak görüşmelerde size rehberlik edilmesi sürecidir. Bu hizmet sayesinde:
            </p>
            <ul className="space-y-3 ml-6">
              <li className="font-sans text-base text-gray-700 flex items-start">
                <i className="ri-checkbox-circle-fill text-accent-gold text-xl mr-3 mt-1"></i>
                <span>Hasarın tam ve doğru bir şekilde tespit edilmesini sağlarız</span>
              </li>
              <li className="font-sans text-base text-gray-700 flex items-start">
                <i className="ri-checkbox-circle-fill text-accent-gold text-xl mr-3 mt-1"></i>
                <span>Sigorta şirketleriyle olan iletişiminizde profesyonel destek alırsınız</span>
              </li>
              <li className="font-sans text-base text-gray-700 flex items-start">
                <i className="ri-checkbox-circle-fill text-accent-gold text-xl mr-3 mt-1"></i>
                <span>Onarım sürecinin en verimli şekilde planlanmasını sağlarız</span>
              </li>
              <li className="font-sans text-base text-gray-700 flex items-start">
                <i className="ri-checkbox-circle-fill text-accent-gold text-xl mr-3 mt-1"></i>
                <span>Gizli hasarların ortaya çıkarılmasını garanti ederiz</span>
              </li>
            </ul>
          </div>

          {/* Image */}
          <div className="mb-16 rounded-2xl overflow-hidden">
            <img
              src="https://readdy.ai/api/search-image?query=automotive%20damage%20assessment%20expert%20examining%20luxury%20car%20body%20damage%20with%20professional%20tools%20and%20tablet%2C%20detailed%20inspection%20process%2C%20modern%20workshop%20environment%2C%20technical%20documentation%2C%20insurance%20claim%20preparation&width=1200&height=600&seq=blog-hasar-002&orientation=landscape"
              alt="Hasar Tespiti"
              className="w-full h-96 object-cover"
            />
          </div>

          {/* Kaza Onarım Süreci */}
          <div className="mb-16">
            <h2 className="font-serif text-3xl font-bold text-gray-900 mb-6">Kaza Onarım Sürecimiz</h2>
            <p className="font-sans text-base text-gray-700 leading-relaxed mb-6">
              Kaza onarımı, sadece görünen hasarların giderilmesinden ibaret değildir. Aracınızın güvenliğini ve performansını etkileyen tüm unsurların detaylı bir şekilde kontrol edilmesi ve onarılması gerekir. Onarım sürecimiz şu aşamalardan oluşur:
            </p>
            
            <div className="space-y-8">
              <div className="bg-gray-50 p-6 rounded-xl">
                <h3 className="font-sans text-xl font-semibold text-gray-900 mb-3 flex items-center">
                  <span className="bg-accent-gold text-white w-8 h-8 rounded-full flex items-center justify-center mr-3 text-sm">1</span>
                  İlk Değerlendirme ve Hasar Tespiti
                </h3>
                <p className="font-sans text-base text-gray-700 leading-relaxed ml-11">
                  Aracınız servisimize geldiğinde, uzman teknisyenlerimiz detaylı bir inceleme yapar. Görünen hasarların yanı sıra, şasi kontrolü, elektronik sistem taraması ve mekanik kontroller yapılır. Bu aşamada tüm hasarlar fotoğraflanır ve raporlanır.
                </p>
              </div>

              <div className="bg-gray-50 p-6 rounded-xl">
                <h3 className="font-sans text-xl font-semibold text-gray-900 mb-3 flex items-center">
                  <span className="bg-accent-gold text-white w-8 h-8 rounded-full flex items-center justify-center mr-3 text-sm">2</span>
                  Sigorta Süreci ve Onay
                </h3>
                <p className="font-sans text-base text-gray-700 leading-relaxed ml-11">
                  Hasar raporu hazırlandıktan sonra, sigorta şirketinizle iletişime geçilir. Gerekli evraklar hazırlanır ve ekspertiz süreci yönetilir. Sigorta onayı alındıktan sonra onarım planlaması yapılır.
                </p>
              </div>

              <div className="bg-gray-50 p-6 rounded-xl">
                <h3 className="font-sans text-xl font-semibold text-gray-900 mb-3 flex items-center">
                  <span className="bg-accent-gold text-white w-8 h-8 rounded-full flex items-center justify-center mr-3 text-sm">3</span>
                  Onarım ve Kalite Kontrol
                </h3>
                <p className="font-sans text-base text-gray-700 leading-relaxed ml-11">
                  Onarım sürecinde orijinal veya OEM parçalar kullanılır. Kaporta, boya, mekanik ve elektronik onarımlar son teknoloji ekipmanlarla gerçekleştirilir. Her aşamada kalite kontrolleri yapılır ve müşterimiz süreç hakkında bilgilendirilir.
                </p>
              </div>

              <div className="bg-gray-50 p-6 rounded-xl">
                <h3 className="font-sans text-xl font-semibold text-gray-900 mb-3 flex items-center">
                  <span className="bg-accent-gold text-white w-8 h-8 rounded-full flex items-center justify-center mr-3 text-sm">4</span>
                  Test Sürüşü ve Teslim
                </h3>
                <p className="font-sans text-base text-gray-700 leading-relaxed ml-11">
                  Onarım tamamlandıktan sonra, aracınız kapsamlı bir test sürüşüne tabi tutulur. Tüm sistemlerin düzgün çalıştığından emin olduktan sonra, aracınız size teslim edilir.
                </p>
              </div>
            </div>
          </div>

          {/* Teknolojimiz */}
          <div className="mb-16">
            <h2 className="font-serif text-3xl font-bold text-gray-900 mb-6">Kullandığımız Teknoloji ve Ekipmanlar</h2>
            <p className="font-sans text-base text-gray-700 leading-relaxed mb-6">
              Modern kaza onarımı, ileri teknoloji ekipmanlar gerektirir. Servisimizde bulunan ekipmanlar:
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="border border-gray-200 p-6 rounded-xl hover:border-accent-gold transition-colors">
                <i className="ri-tools-line text-3xl text-accent-gold mb-3"></i>
                <h4 className="font-sans text-lg font-semibold text-gray-900 mb-2">Şasi Düzeltme Sistemleri</h4>
                <p className="font-sans text-sm text-gray-600">Lazer ölçüm teknolojisi ile milimetrik hassasiyette şasi düzeltme</p>
              </div>
              <div className="border border-gray-200 p-6 rounded-xl hover:border-accent-gold transition-colors">
                <i className="ri-paint-brush-line text-3xl text-accent-gold mb-3"></i>
                <h4 className="font-sans text-lg font-semibold text-gray-900 mb-2">Fırın Boya Kabini</h4>
                <p className="font-sans text-sm text-gray-600">Orijinal renk eşleştirme ve kusursuz boya uygulaması</p>
              </div>
              <div className="border border-gray-200 p-6 rounded-xl hover:border-accent-gold transition-colors">
                <i className="ri-scan-line text-3xl text-accent-gold mb-3"></i>
                <h4 className="font-sans text-lg font-semibold text-gray-900 mb-2">3D Ölçüm Sistemleri</h4>
                <p className="font-sans text-sm text-gray-600">Hasar tespiti ve onarım sonrası kontrol için 3D tarama</p>
              </div>
              <div className="border border-gray-200 p-6 rounded-xl hover:border-accent-gold transition-colors">
                <i className="ri-computer-line text-3xl text-accent-gold mb-3"></i>
                <h4 className="font-sans text-lg font-semibold text-gray-900 mb-2">Elektronik Teşhis Cihazları</h4>
                <p className="font-sans text-sm text-gray-600">Tüm elektronik sistemlerin kontrolü ve kalibrasyonu</p>
              </div>
            </div>
          </div>

          {/* CTA Section */}
          <div className="bg-primary-dark rounded-2xl p-10 text-center">
            <h3 className="font-serif text-3xl font-bold text-white mb-4">Aracınız Kaza Geçirdi mi?</h3>
            <p className="font-sans text-base text-gray-300 mb-8 max-w-2xl mx-auto">
              Hemen bizimle iletişime geçin. Hasar danışmanlığı ve onarım sürecinde profesyonel destek alın. Sigorta işlemlerinizden onarıma kadar her aşamada yanınızdayız.
            </p>
            <a
              href="/#appointment"
              className="inline-block bg-accent-gold text-primary-dark px-8 py-4 rounded-lg font-sans font-semibold hover:bg-white transition-colors whitespace-nowrap cursor-pointer"
            >
              Hemen Randevu Alın
            </a>
          </div>

        </div>
      </section>

      <Footer />
      <WhatsAppButton />
    </div>
  );
}